import React, { useState, useEffect } from 'react';
import { Check, RefreshCw, AlertTriangle, Save, Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { supabase } from '../../lib/supabase';

const WhatsAppConfigurationManagement = () => {
  const [config, setConfig] = useState({
    phoneNumber: '',
    accessToken: '',
    webhookUrl: '',
    webhookVerifyToken: '',
    apiVersion: 'v18.0',
    rateLimit: 80,
    retryAttempts: 3,
    retryDelay: 5000
  });

  const [templates, setTemplates] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    lastChecked: null,
    errorMessage: ''
  });

  const [showToken, setShowToken] = useState(false);
  const [showWebhookToken, setShowWebhookToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copied, setCopied] = useState('');

  useEffect(() => {
    loadConfiguration();
    loadTemplates();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      
      // Safely check if Supabase is available
      if (!supabase) {
        console.warn('Supabase client not available');
        setMessage({ type: 'error', text: 'Configuration de la base de données non disponible' });
        setLoading(false);
        return;
      }
      
      // Load configuration from whatsapp_webhook_config table (correct table name)
      const { data, error } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.eq('provider', 'whatsapp')?.eq('is_active', true)?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error loading configuration:', error);
        setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
      }

      if (data) {
        // Map database fields to config state
        setConfig({
          phoneNumber: data?.config_data?.phoneNumber || '',
          accessToken: data?.api_key || '',
          webhookUrl: data?.webhook_url || '',
          webhookVerifyToken: data?.config_data?.webhookVerifyToken || '',
          apiVersion: data?.config_data?.apiVersion || 'v18.0',
          rateLimit: data?.config_data?.rateLimit || 80,
          retryAttempts: data?.config_data?.retryAttempts || 3,
          retryDelay: data?.config_data?.retryDelay || 5000
        });
      }

      // Check connection status
      await checkConnection();
    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // Safely check if Supabase is available
      if (!supabase) {
        console.warn('Supabase client not available for templates');
        setTemplates([]);
        return;
      }

      const { data, error } = await supabase?.from('whatsapp_message_templates')?.select('*')?.order('shipment_status');

      if (error) {
        console.error('Error loading templates:', error);
        setTemplates([]);
        return;
      }

      // Transform database data to match UI expectations
      const transformedTemplates = data?.map(template => ({
        id: template?.id,
        template_name: template?.shipment_status, // Map shipment_status to template_name for UI
        display_name: template?.shipment_status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
        message_template: template?.message_fr || template?.message_en, // Use French or fallback to English
        message_fr: template?.message_fr,
        message_en: template?.message_en,
        status: template?.is_active ? 'active' : 'inactive',
        is_active: template?.is_active,
        variables: [] // Add empty variables array if not present
      })) || [];

      setTemplates(transformedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      // Fallback to empty array if error
      setTemplates([]);
    }
  };

  const checkConnection = async () => {
    try {
      setTesting(true);
      
      // Only test connection if we have an access token
      if (!config?.accessToken) {
        setConnectionStatus({
          isConnected: false,
          lastChecked: new Date(),
          errorMessage: 'Token d\'accès manquant'
        });
        return false;
      }
      
      // Simulate API health check
      const response = await fetch(`https://graph.facebook.com/${config?.apiVersion}/health`, {
        headers: {
          'Authorization': `Bearer ${config?.accessToken}`
        }
      });

      const isConnected = response?.ok;
      
      setConnectionStatus({
        isConnected,
        lastChecked: new Date(),
        errorMessage: isConnected ? '' : 'Connexion échouée'
      });

      return isConnected;
    } catch (error) {
      setConnectionStatus({
        isConnected: false,
        lastChecked: new Date(),
        errorMessage: error?.message || 'Erreur de connexion'
      });
      return false;
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);

      if (!supabase) {
        throw new Error('Base de données non disponible');
      }

      // Check if configuration exists
      const { data: existing } = await supabase?.from('whatsapp_webhook_config')?.select('id')?.eq('provider', 'whatsapp')?.single();

      const configData = {
        provider: 'whatsapp',
        webhook_url: config?.webhookUrl,
        api_key: config?.accessToken,
        is_active: true,
        config_data: {
          phoneNumber: config?.phoneNumber,
          webhookVerifyToken: config?.webhookVerifyToken,
          apiVersion: config?.apiVersion,
          rateLimit: config?.rateLimit,
          retryAttempts: config?.retryAttempts,
          retryDelay: config?.retryDelay
        },
        updated_at: new Date()?.toISOString()
      };

      // Declare error variable before use - Add this line
      let error;
      
      if (existing) {
        // Update existing configuration
        const result = await supabase?.from('whatsapp_webhook_config')?.update(configData)?.eq('id', existing?.id);
        error = result?.error;
      } else {
        // Insert new configuration
        const result = await supabase?.from('whatsapp_webhook_config')?.insert([configData]);
        error = result?.error;
      }

      if (error) throw error;

      setMessage({ type: 'success', text: 'Configuration enregistrée avec succès' });
      
      // Test connection after saving
      await checkConnection();
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement: ' + error?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    const isConnected = await checkConnection();
    setMessage({
      type: isConnected ? 'success' : 'error',
      text: isConnected ? 'Connexion établie avec succès' : 'Échec de la connexion'
    });
  };

  const handleCopyWebhookUrl = () => {
    if (navigator?.clipboard) {
      navigator.clipboard?.writeText(config?.webhookUrl);
      setCopied('webhook');
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const handleUpdateTemplate = async (templateId, updates) => {
    try {
      if (!supabase) {
        throw new Error('Base de données non disponible');
      }

      // Transform UI updates to database format
      const dbUpdates = {};
      
      if (updates?.status !== undefined) {
        dbUpdates.is_active = updates?.status === 'active';
      }
      
      if (updates?.message_template !== undefined) {
        dbUpdates.message_fr = updates?.message_template;
      }

      const { error } = await supabase?.from('whatsapp_message_templates')?.update(dbUpdates)?.eq('id', templateId);

      if (error) throw error;

      // Update local state with transformed data
      setTemplates(templates?.map(t => 
        t?.id === templateId ? { 
          ...t, 
          ...updates,
          is_active: dbUpdates?.is_active !== undefined ? dbUpdates?.is_active : t?.is_active
        } : t
      ));

      setMessage({ type: 'success', text: 'Template mis à jour' });
    } catch (error) {
      console.error('Error updating template:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour du template' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2d5f3f]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Configuration WhatsApp</h1>
          <p className="mt-2 text-gray-600">
            Gérez les paramètres de l'API WhatsApp Business et les templates de notifications
          </p>
        </div>

        {/* Message Alert */}
        {message?.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message?.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message?.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            )}
            <p>{message?.text}</p>
          </div>
        )}

        {/* Connection Status */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Statut de Connexion</h2>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  connectionStatus?.isConnected ? 'bg-green-500' : 'bg-red-500'
                } animate-pulse`}></div>
                <span className={`font-medium ${
                  connectionStatus?.isConnected ? 'text-green-700' : 'text-red-700'
                }`}>
                  {connectionStatus?.isConnected ? 'Connecté' : 'Déconnecté'}
                </span>
                {connectionStatus?.lastChecked && (
                  <span className="text-sm text-gray-500">
                    Dernière vérification: {new Date(connectionStatus.lastChecked)?.toLocaleTimeString('fr-FR')}
                  </span>
                )}
              </div>
              {connectionStatus?.errorMessage && (
                <p className="mt-2 text-sm text-red-600">{connectionStatus?.errorMessage}</p>
              )}
            </div>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="px-4 py-2 bg-[#2d5f3f] text-white rounded-lg hover:bg-[#234a32] disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
              Tester la Connexion
            </button>
          </div>
        </div>

        {/* API Configuration */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration API</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de Téléphone WhatsApp Business
              </label>
              <input
                type="text"
                value={config?.phoneNumber}
                onChange={(e) => setConfig({ ...config, phoneNumber: e?.target?.value })}
                placeholder="+33612345678"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version API
              </label>
              <select
                value={config?.apiVersion}
                onChange={(e) => setConfig({ ...config, apiVersion: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
              >
                <option value="v18.0">v18.0</option>
                <option value="v17.0">v17.0</option>
                <option value="v16.0">v16.0</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Token
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={config?.accessToken}
                  onChange={(e) => setConfig({ ...config, accessToken: e?.target?.value })}
                  placeholder="EAAxxxxxxxxx..."
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={config?.webhookUrl}
                  onChange={(e) => setConfig({ ...config, webhookUrl: e?.target?.value })}
                  placeholder="https://votre-domaine.com/webhook/whatsapp"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleCopyWebhookUrl}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {copied === 'webhook' ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook Verify Token
              </label>
              <div className="relative">
                <input
                  type={showWebhookToken ? 'text' : 'password'}
                  value={config?.webhookVerifyToken}
                  onChange={(e) => setConfig({ ...config, webhookVerifyToken: e?.target?.value })}
                  placeholder="votre_token_secret"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookToken(!showWebhookToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showWebhookToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Paramètres Avancés</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Limite de Taux (msg/min)
                </label>
                <input
                  type="number"
                  value={config?.rateLimit}
                  onChange={(e) => setConfig({ ...config, rateLimit: parseInt(e?.target?.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tentatives de Retry
                </label>
                <input
                  type="number"
                  value={config?.retryAttempts}
                  onChange={(e) => setConfig({ ...config, retryAttempts: parseInt(e?.target?.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Délai Retry (ms)
                </label>
                <input
                  type="number"
                  value={config?.retryDelay}
                  onChange={(e) => setConfig({ ...config, retryDelay: parseInt(e?.target?.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2d5f3f] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveConfiguration}
              disabled={saving}
              className="px-6 py-2 bg-[#2d5f3f] text-white rounded-lg hover:bg-[#234a32] disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Enregistrement...' : 'Enregistrer la Configuration'}
            </button>
          </div>
        </div>

        {/* Templates Management */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Templates de Notification</h2>
          
          <div className="space-y-4">
            {templates?.map((template) => (
              <div key={template?.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{template?.display_name}</h3>
                    <p className="text-sm text-gray-500 mt-1">Type: {template?.template_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      template?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template?.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                    <button
                      onClick={() => handleUpdateTemplate(template?.id, { 
                        status: template?.status === 'active' ? 'inactive' : 'active' 
                      })}
                      className="text-sm text-[#2d5f3f] hover:text-[#234a32]"
                    >
                      {template?.status === 'active' ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded p-3 mb-3">
                  <p className="text-sm text-gray-700">{template?.message_template}</p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-600">Variables:</span>
                  {template?.variables?.map((variable) => (
                    <span 
                      key={variable}
                      className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                    >
                      {`{${variable}}`}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConfigurationManagement;